/**
 * Task Types API
 * Handles all task type-related API calls
 */
import apiClient from './apiClient';

export interface TaskType {
  id: number;
  name: string;
  code: string | null;
  category: string | null;
  description: string | null;
  is_leave_type: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface TaskTypesListResponse {
  tasks: TaskType[];
  total: number;
}

export interface TaskTypesQueryParams {
  include_inactive?: boolean;
  category?: string;
}

export const taskTypesApi = {
  /**
   * Get all active task types
   */
  getTaskTypes: async (params?: TaskTypesQueryParams): Promise<TaskTypesListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.include_inactive) queryParams.append('include_inactive', 'true');
    if (params?.category) queryParams.append('category', params.category);

    const query = queryParams.toString();
    return apiClient.get<TaskTypesListResponse>(`/tasks${query ? `?${query}` : ''}`);
  },

  /**
   * Get a specific task type by ID
   */
  getTaskType: async (taskId: number): Promise<{ task: TaskType }> => {
    return apiClient.get<{ task: TaskType }>(`/tasks/${taskId}`);
  },

  /**
   * Create a new task type (SuperAdmin only)
   */
  createTaskType: async (data: {
    name: string;
    code?: string;
    category?: string;
    description?: string;
    is_leave_type?: boolean;
  }): Promise<{ message: string; task: TaskType }> => {
    return apiClient.post('/tasks', data);
  },

  /**
   * Update a task type (SuperAdmin only)
   */
  updateTaskType: async (taskId: number, data: Partial<{
    name: string;
    code: string;
    category: string;
    description: string;
    is_leave_type: boolean;
    is_active: boolean;
  }>): Promise<{ message: string; task: TaskType }> => {
    return apiClient.put(`/tasks/${taskId}`, data);
  },

  /**
   * Delete a task type (SuperAdmin only)
   */
  deleteTaskType: async (taskId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/tasks/${taskId}`);
  },

  /**
   * Seed default task types (SuperAdmin only)
   */
  seedTaskTypes: async (): Promise<{ message: string; created: number; skipped: number }> => {
    return apiClient.post('/tasks/seed');
  },
};

export default taskTypesApi;
